import { WithApiRequestMeta } from "../../api-results";
import { RtkEmptySplitApi } from "../../types";
import { SubscriptionTags } from "./tags";
import {
  AdjustAddonRequest,
  ChangePlanRequest,
  CreatePortalSessionRequest,
  CreatePortalSessionResponse,
  CreateSubscriptionRequest,
  PlansResponse,
  PreviewInvoiceResponse,
  SubscriptionGetResponse,
  SubscriptionInvoicesResponse,
  SubscriptionResponseMode,
  SupportedAddonsResponse,
  TestClockResponse,
  TopupConfigResponse,
  TopupNowRequest,
  TopupNowResponse,
  UpdateTopupConfigRequest,
} from "./types";

export const addSubscriptionV2Api = (emptySplitApi: RtkEmptySplitApi) => {
  const apiWithTags = emptySplitApi.enhanceEndpoints({
    addTagTypes: [
      SubscriptionTags.addons,
      SubscriptionTags.invoices,
      SubscriptionTags.invoicePreview,
      SubscriptionTags.plans,
      SubscriptionTags.subscriptionDetails,
      SubscriptionTags.upcomingInvoices,
      SubscriptionTags.topupConfig,
    ],
  });
  const subscriptionV2Api = apiWithTags.injectEndpoints({
    overrideExisting: true,
    endpoints: (build) => ({
      getSubscription: build.query<
        SubscriptionGetResponse,
        WithApiRequestMeta<{
          entityId: string;
          params: {
            mode: SubscriptionResponseMode;
          };
        }>
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/${entityId}/billing/subscription`,
          method: "GET",
          params,
        }),
        providesTags: [SubscriptionTags.subscriptionDetails],
      }),
      listPlans: build.query<
        PlansResponse,
        WithApiRequestMeta<{
          entityId: string;
          params: {
            type: string;
          };
        }>
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/${entityId}/billing/plans`,
          method: "GET",
          params,
        }),
        providesTags: [SubscriptionTags.plans],
      }),
      listAddons: build.query<
        SupportedAddonsResponse,
        WithApiRequestMeta<{
          entityId: string;
          params: {
            plan_id: string;
          };
        }>
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/${entityId}/billing/addons`,
          method: "GET",
          params,
        }),
        providesTags: [SubscriptionTags.addons],
      }),
      setupIntent: build.query<
        {
          client_secret: string;
          id: string;
          pub_key: string;
          status: string;
        },
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/billing/setup_intent`,
          method: "GET",
        }),
      }),
      editSubscription: build.mutation<
        Record<string, never>,
        WithApiRequestMeta<{
          entityId: string;
          data: CreateSubscriptionRequest;
        }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/billing/subscription`,
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          data,
        }),
        invalidatesTags: [
          SubscriptionTags.subscriptionDetails,
          SubscriptionTags.invoices,
          SubscriptionTags.invoicePreview,
        ],
      }),
      changePlan: build.mutation<
        Record<string, never>,
        WithApiRequestMeta<{ entityId: string; data: ChangePlanRequest }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/billing/change_plan`,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          data,
        }),
        invalidatesTags: [
          SubscriptionTags.invoices,
          SubscriptionTags.invoicePreview,
        ],
      }),
      adjustAddon: build.mutation<
        Record<string, never>,
        WithApiRequestMeta<{ entityId: string; data: AdjustAddonRequest }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/billing/addons/adjust`,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          data,
        }),
        invalidatesTags: [
          SubscriptionTags.invoices,
          SubscriptionTags.invoicePreview,
        ],
      }),
      deleteSubscriptionItem: build.mutation<
        void,
        WithApiRequestMeta<{ entityId: string; itemId: string }>
      >({
        query: ({ entityId, itemId }) => ({
          url: `/api/auth/${entityId}/billing/subscription/item/${itemId}`,
          method: "DELETE",
        }),
        invalidatesTags: [
          SubscriptionTags.subscriptionDetails,
          SubscriptionTags.invoicePreview,
        ],
      }),
      listInvoices: build.query<
        SubscriptionInvoicesResponse,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/billing/invoices`,
          method: "GET",
        }),
        providesTags: [SubscriptionTags.invoices],
      }),
      previewInvoice: build.query<
        PreviewInvoiceResponse,
        WithApiRequestMeta<{ entityId: string; params: { price_id: string } }>
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/${entityId}/billing/invoices/preview`,
          method: "GET",
          params,
        }),
        providesTags: [SubscriptionTags.invoicePreview],
      }),
      upcomingInvoice: build.query<
        void,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          // Note: path uses upcoming_inoice (typo preserved from implementation).
          url: `/api/auth/${entityId}/billing/upcoming_inoice`,
          method: "GET",
        }),
        providesTags: [SubscriptionTags.upcomingInvoices],
      }),
      cancelSubscription: build.mutation<
        void,
        WithApiRequestMeta<{ entityId: string; data?: { feedback?: string } }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/billing/subscription`,
          method: "DELETE",
          data,
        }),
        invalidatesTags: [SubscriptionTags.invoicePreview],
      }),
      createPortalSession: build.mutation<
        CreatePortalSessionResponse,
        WithApiRequestMeta<{
          entityId: string;
          data: CreatePortalSessionRequest;
        }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/billing/portal-session?return_url=${data.return_url}`,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          // data,
        }),
      }),
      setupIntentCallback: build.mutation<
        void,
        WithApiRequestMeta<{ setupIntentId: string }>
      >({
        query: ({ setupIntentId }) => ({
          url: `/stripe/callback/setup_intent`,
          method: "GET",
          params: { setup_intent: setupIntentId },
        }),
        invalidatesTags: [
          SubscriptionTags.subscriptionDetails,
          SubscriptionTags.invoicePreview,
        ],
      }),
      getTestClock: build.query<
        TestClockResponse,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/billing/test_clock`,
          method: "GET",
        }),
      }),
      advanceTestClock: build.mutation<
        { result: string },
        WithApiRequestMeta<{ entityId: string; data: { days: number } }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/billing/test_clock`,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          data,
        }),
      }),
      getTopupConfig: build.query<
        TopupConfigResponse,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/billing/topup`,
          method: "GET",
        }),
        providesTags: [SubscriptionTags.topupConfig],
      }),
      updateTopupConfig: build.mutation<
        TopupConfigResponse,
        WithApiRequestMeta<{ entityId: string; data: UpdateTopupConfigRequest }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/billing/topup`,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          data,
        }),
        invalidatesTags: [
          SubscriptionTags.subscriptionDetails,
          SubscriptionTags.invoicePreview,
        ],
      }),
      topupNow: build.mutation<
        TopupNowResponse,
        WithApiRequestMeta<{ entityId: string; data: TopupNowRequest }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/billing/topup/now`,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          data,
        }),
        invalidatesTags: [SubscriptionTags.invoicePreview],
      }),
      testUpdateUsage: build.mutation<
        Record<string, never>,
        WithApiRequestMeta<{ entityId: string; data: { usage: number } }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/billing/test/update`,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          data,
        }),
        invalidatesTags: [
          SubscriptionTags.subscriptionDetails,
          SubscriptionTags.invoicePreview,
        ],
      }),
    }),
  });

  return subscriptionV2Api;
};

export type SubscriptionV2Api = ReturnType<typeof addSubscriptionV2Api>;
